import { useState } from 'react';

import { Badge, Button, Group, Stack, Text, TextInput } from '@mantine/core';

import { AddressStatus, NotificationAddress } from '@/types/team-settings';

interface CopyAddressManagerProps {
  addresses: NotificationAddress[];
  /** Each of these presents its own failures and resolves either way. */
  onRegister: (email: string) => Promise<boolean>;
  onResend: (addressId: string) => Promise<void>;
  onRemove: (addressId: string) => Promise<void>;
}

const STATUS: Record<AddressStatus, { label: string; colour: string }> = {
  verified: { label: 'Confirmed', colour: 'green' },
  pending: { label: 'Awaiting confirmation', colour: 'yellow' },
  expired: { label: 'Link expired', colour: 'red' },
};

// A light check to catch typos; the server validates properly.
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The shared inboxes a team copies notifications to. An address receives
 * nothing until its owner has confirmed it from a link emailed to it, so each
 * one shows where it stands and can have that link resent.
 */
export function CopyAddressManager({
  addresses,
  onRegister,
  onResend,
  onRemove,
}: CopyAddressManagerProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState<string | null>(null);

  const add = async () => {
    const value = email.trim();
    if (!LOOKS_LIKE_EMAIL.test(value)) {
      setError('Enter a valid email address');
      return;
    }
    setError(null);
    setAdding(true);
    try {
      if (await onRegister(value)) {
        setEmail('');
      }
    } finally {
      setAdding(false);
    }
  };

  const act = async (id: string, action: () => Promise<void>) => {
    setBusyId(id);
    try {
      await action();
    } finally {
      setBusyId(null);
      setConfirmingRemoval(null);
    }
  };

  return (
    <Stack gap="xs" data-testid="copy-address-manager">
      <Text size="sm" c="dimmed">
        A shared inbox that can be copied on notifications. It is sent a link to confirm the address
        and receives nothing until that is clicked.
      </Text>

      {addresses.map((address) => {
        const status = STATUS[address.status];
        const busy = busyId === address.id;
        return (
          <Group
            key={address.id}
            justify="space-between"
            wrap="nowrap"
            data-testid={`address-${address.email}`}
          >
            <Group gap="xs" wrap="nowrap" miw={0}>
              <Text size="sm" truncate>
                {address.email}
              </Text>
              <Badge size="xs" color={status.colour} variant="light">
                {status.label}
              </Badge>
            </Group>
            {confirmingRemoval === address.id ? (
              <Group gap={4} wrap="nowrap">
                <Text size="xs">Remove?</Text>
                <Button
                  size="compact-xs"
                  color="red"
                  loading={busy}
                  onClick={() => void act(address.id, () => onRemove(address.id))}
                >
                  Yes, remove
                </Button>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="gray"
                  onClick={() => setConfirmingRemoval(null)}
                >
                  Cancel
                </Button>
              </Group>
            ) : (
              <Group gap={4} wrap="nowrap">
                {address.status !== 'verified' && (
                  <Button
                    size="compact-xs"
                    variant="light"
                    loading={busy}
                    onClick={() => void act(address.id, () => onResend(address.id))}
                    aria-label={`Resend confirmation to ${address.email}`}
                  >
                    Resend
                  </Button>
                )}
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="gray"
                  disabled={busy}
                  onClick={() => setConfirmingRemoval(address.id)}
                  aria-label={`Remove ${address.email}`}
                >
                  Remove
                </Button>
              </Group>
            )}
          </Group>
        );
      })}

      <Group align="flex-start" gap="xs" wrap="nowrap">
        <TextInput
          aria-label="Copy address"
          placeholder="shared-inbox@client.com"
          value={email}
          onChange={(e) => {
            setEmail(e.currentTarget.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              void add();
            }
          }}
          error={error}
          disabled={adding}
          style={{ flex: 1 }}
        />
        <Button size="sm" onClick={() => void add()} loading={adding}>
          Add and send confirmation
        </Button>
      </Group>
    </Stack>
  );
}
